import React from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DevicesIcon from "@mui/icons-material/Devices";
import BusinessIcon from "@mui/icons-material/Business";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InboxIcon from "@mui/icons-material/Inbox";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PropaneTankOutlinedIcon from "@mui/icons-material/PropaneTankOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import WaterDamageOutlinedIcon from "@mui/icons-material/WaterDamageOutlined";

// Each navigable item is gated against the caller's afms portal capabilities
// (resolved by GET /api/portal/my-capabilities — the SAME role + permission
// matrix the [PortalAuthorize] filter enforces). An item shows only when the
// user can actually open its page:
//   requiredAction        — a PortalAction the user's role must be permitted
//   requiresPlatformAdmin — the user must be a platform admin
//   requiresVendorOrg     — the org must be flagged isVendorOrganization
// "ViewAsset" is the baseline "has portal access" action; every real portal
// role has it. Items with no gate are always shown.
//
// Layout (post-Environmental refactor): the Environmental Control Center is
// the always-visible primary entry for any org with the "Environmental"
// system. The Command Center is on/off per org via the backoffice
// showCommandCenter toggle alone: any org can have both customers and vendors,
// so isVendorOrganization doesn't gate it (Jam 0c74b41e, 2026-11-03). Everything
// else that used to live under a "Legacy" collapsable is now unreachable
// from the sidebar — routes still exist for deep links but no menu entry —
// except the five tank pages, which are back under the EEC.
const primaryRoutes = [
  {
    // EEC: clickable header that navigates to the EEC page, with `Tickets`
    // permanently visible underneath it as an indented child. `noCollapse:true`
    // is the sidebar renderer's flag for "navigate on click + always show
    // children" (no expand arrow). See Sidenav/index.js:281.
    type: "collapse",
    name: "Environmental Control Center",
    key: "vendor-nav-env-control-center",
    route: "/afms/environmental-command-center",
    noCollapse: true,
    icon: <EnergySavingsLeafIcon fontSize="small" />,
    requiresEccVisible: true,
    collapse: [
      {
        name: "Tickets",
        key: "vendor-nav-eec-tickets",
        route: "/afms/settings/tickets",
        icon: <ConfirmationNumberIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // Payables — invoices sent TO this org by various vendors.
        // Lives beneath Tickets inside the EEC collapse: that's where
        // dealers/operators triage what they owe.
        name: "Payables",
        key: "vendor-nav-eec-payables",
        route: "/payables",
        icon: <RequestQuoteOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // AP queue (P3) — the received pickup invoices this user processes:
        // waiting approval, ready to send, with AP to pay, paid. Limited
        // server-side to the locations their AP role covers.
        name: "AP queue",
        key: "vendor-nav-eec-ap-queue",
        route: "/afms/ap-queue",
        icon: <AccountBalanceIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
        // Only for those it serves (owners, AP contacts, AP Approvers): a
        // service manager opened it and got its refusal (QC 2026-10-27).
        requiresApAccess: true,
      },
      {
        // Pickups — this org's own pickup history (tank + bin), scoped by
        // site and a request-date window. Sits right after Payables: the
        // client's read-only record of what was picked up, alongside what it
        // was billed for. Server-side the org scope comes from the token, so
        // a client can only ever see its own pickups.
        name: "Pickups",
        key: "vendor-nav-eec-pickups",
        route: "/pickups",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // Audit & EPA export (P4) — every pickup completed in a period, as one
        // zip with a summary report, each pickup's record, files and invoices,
        // and a hash of every file.
        name: "Audit & EPA export",
        key: "vendor-nav-eec-audit-export",
        route: "/afms/audit-export",
        icon: <FactCheckOutlinedIcon fontSize="small" />,
        // ViewAsset like its neighbours: the matrix's ExportAuditPack isn't
        // granted to DealerAdmin, while the server's gate (PortalAuthorize) lets
        // every resolved role through for now.
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // Waste stream reports (P5) — how much, when, costs, payments and
        // credits by stream, vendor, region and location, with SLA benchmarks.
        name: "Reports",
        key: "vendor-nav-eec-waste-reports",
        route: "/afms/reports/waste-streams",
        icon: <InsightsOutlinedIcon fontSize="small" />,
        // ViewAsset for the same reason as the export above: the server gates on
        // ViewFinancialReports, which PortalAuthorize lets every resolved role through.
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // Approval Inbox — pending approval requests routed to this user
        // (afms-api /api/approvals). Indented under EEC right after
        // Payables so triage flows top-to-bottom: tickets → payables to
        // pay → things needing your sign-off.
        name: "Approvals",
        key: "vendor-nav-eec-approvals",
        route: "/afms/approvals/inbox",
        icon: <InboxIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      // The tank pages from the old "Tanks" menu (afms/routes.js), back in the
      // sidebar 2026-10-27. Tony: "Cannot find the device id's for all the oil
      // systems" — All tanks lists every tank with its device ID.
      {
        name: "All tanks",
        key: "vendor-nav-eec-tanks",
        route: "/afms/tanks",
        icon: <PropaneTankOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        name: "Oil inventory",
        key: "vendor-nav-eec-oil-inventory",
        route: "/afms/inventory/oil",
        icon: <OpacityOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // The reporting page, not the old menu's /afms/inventory/oil-audit,
        // which only ever showed placeholders.
        name: "Oil audit",
        key: "vendor-nav-eec-oil-audit",
        route: "/afms/reporting/oil-audit",
        icon: <PlaylistAddCheckOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        name: "Event history",
        key: "vendor-nav-eec-event-history",
        route: "/afms/inventory/events",
        icon: <HistoryOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        name: "Leak detection",
        key: "vendor-nav-eec-leak-detection",
        route: "/afms/inventory/leaks",
        icon: <WaterDamageOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      {
        // The AFMS Ordering Wizard: spec each fluid-management unit (tanks, sensors,
        // controller, operators, valves) and send Flex2 a quote request for them. Only
        // reachable from the SPCC calculator until 2026-11-01, when Ciocca needed a quote
        // for 10 units they spec out themselves.
        name: "Spec & quote equipment",
        key: "vendor-nav-eec-ordering-wizard",
        route: "/afms/ordering-wizard",
        icon: <RequestQuoteOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresEccVisible: true,
      },
      // Approval Routing Rules + the legacy Asset Setup Wizard used to
      // live here as siblings of the Approvals inbox. Rules moved into
      // Settings → Approvals (admin surface); the Setup Wizard was
      // retired from the menu — the wizard route still exists at
      // /afms/setup/workflows/new for direct links from ConfigCompleteness
      // and the AssetWorkflowTab CTA.
    ],
  },
  {
    // Vendor Command Center: clickable header with `Devices` indented
    // underneath, same noCollapse pattern EEC uses. Parent and children all
    // carry requiresCommandCenterVisible, so the org's toggle hides the branch.
    type: "collapse",
    name: "Command Center",
    key: "vendor-nav-command-center",
    route: "/afms/command-center",
    noCollapse: true,
    icon: <HomeOutlinedIcon fontSize="small" />,
    requiredAction: "ViewAsset",
    requiresCommandCenterVisible: true,
    collapse: [
      {
        name: "Client Assets",
        key: "vendor-nav-cc-client-assets",
        route: "/vendor/devices",
        icon: <DevicesIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresCommandCenterVisible: true,
      },
      {
        // Receivables — invoices my vendor org has issued to client
        // orgs. Lives under Vendor Command Center because this is the
        // vendor's outgoing-billing inbox. Gated like its parent.
        name: "Receivables",
        key: "vendor-nav-cc-receivables",
        route: "/receivables",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresCommandCenterVisible: true,
      },
      {
        // Pickups — every pickup the vendor was assigned, across all its
        // client orgs, filterable by client org → site → request-date window.
        // Open rows carry their next step (Confirm, Schedule, …), and the
        // badge counts requests still waiting to be confirmed: QC 2026-10-23,
        // this is where a vendor looks first for a new request.
        name: "Pickups",
        key: "vendor-nav-cc-pickups",
        route: "/vendor/pickups",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        badge: "pickupsAwaiting",
        requiredAction: "ViewAsset",
        requiresCommandCenterVisible: true,
      },
    ],
  },
  {
    // Personal cross-system inbox — top-level, visible to any org with
    // environmental portal access (not vendor-gated). Clickable header,
    // no children (noCollapse). Backed by notifications-api
    // /api/notifications/inbox. `badge` asks the sidebar to show a live
    // count beside the label; "inbox" is the unread count of that same feed.
    type: "collapse",
    name: "My Inbox",
    key: "vendor-nav-inbox",
    route: "/afms/inbox",
    noCollapse: true,
    icon: <MailOutlineIcon fontSize="small" />,
    requiredAction: "ViewAsset",
    badge: "inbox",
  },
];

const settingsGroup = {
  // AFMS-rebranded Settings group. Sub-items lift their data from the existing
  // flex2 Org Admin / Assets / Devices pages but render in AFMS shell + are
  // scoped to the caller's own org via JWT (backend `[AllowOwnOrgAccess]`).
  type: "collapse",
  name: "Settings",
  key: "vendor-nav-settings",
  icon: <SettingsOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Organization",
      key: "vendor-nav-settings-organization",
      route: "/afms/settings/organization",
      icon: <BusinessIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Users",
      key: "vendor-nav-settings-users",
      route: "/afms/settings/users",
      icon: <PeopleAltIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Assets",
      key: "vendor-nav-settings-assets",
      route: "/afms/settings/assets",
      icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Devices",
      key: "vendor-nav-settings-devices",
      route: "/afms/settings/devices",
      icon: <DevicesIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      // Vendor and customer onboarding (WS84): W-9s, insurance and contracts
      // with each vendor and customer, their service agreements, and the tanks
      // and bins each vendor reaches by dealership. For every org — a managed
      // vendor (no Management Core menu) uploads its own paperwork here.
      name: "Onboarding",
      key: "vendor-nav-settings-onboarding",
      route: "/afms/settings/onboarding",
      icon: <AssignmentIndOutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      // Approvals admin surface — currently just the Approval Routing
      // Rules page; future Approver Roles / Approval Policy templates
      // pages land here as siblings under the same collapse.
      type: "collapse",
      name: "Approvals",
      key: "vendor-nav-settings-approvals",
      icon: <RuleFolderOutlinedIcon fontSize="small" />,
      collapse: [
        {
          name: "Approval Rules",
          key: "vendor-nav-settings-approval-rules",
          route: "/afms/settings/approval-rules",
          requiredAction: "ViewAsset",
        },
        {
          // Approval Gates (WS33/35) — pin one of the org's Approval
          // Rules to each system event (Pickup.Request,
          // Invoice.SendToAp …). Gates fire only when an active
          // assignment exists. Pickup.Dispatch is set per service
          // agreement (WS83).
          name: "Approval Gates",
          key: "vendor-nav-settings-approval-gates",
          route: "/afms/settings/approval-gates",
          requiredAction: "ViewAsset",
        },
        {
          // Approval Notifications (WS39) — per-user opt-in for email
          // / sms when a new approval is assigned to them; engine
          // routes to notifications-api at instance creation.
          name: "Approval Notifications",
          key: "vendor-nav-settings-approval-notifications",
          route: "/afms/settings/approval-notifications",
          requiredAction: "ViewAsset",
        },
      ],
    },
    {
      // Environmental Settings — org-level environmental configuration.
      // Nested collapse (starts collapsed on load); Pickup Workflow is the
      // first page, more land here as siblings.
      type: "collapse",
      name: "Environmental Settings",
      key: "vendor-nav-settings-environmental",
      icon: <EnergySavingsLeafOutlinedIcon fontSize="small" />,
      collapse: [
        {
          // A CLIENT page: pickup setup for the org's own tanks and bins.
          // Gated like the Environmental Control Center, the org's
          // client-side environmental flag — a vendor-only org has neither
          // (QC 2026-10-17: Valicor saw "No environmental assets found").
          // Its clients do the setup; it answers on Workflow Notifications.
          // Coupling: this reads Organization.showEnvironmentalControlCenter
          // as "has the client side". flex2-api's EnterpriseOrgService sets it
          // false for orgs created with kind "vendor"; a super admin can also
          // hide the ECC by hand, which then hides this page too.
          name: "Pickup Workflow",
          key: "vendor-nav-settings-pickup-workflow",
          route: "/afms/settings/pickup-workflow",
          requiredAction: "ViewAsset",
          requiresEccVisible: true,
        },
        {
          // A CLIENT page, gated like Pickup Workflow: what the org has agreed
          // with each vendor per waste stream (WS83) — today, whether dispatch
          // waits for its approval.
          name: "Service Agreements",
          key: "vendor-nav-settings-service-agreements",
          route: "/afms/settings/service-agreements",
          requiredAction: "ViewAsset",
          requiresEccVisible: true,
        },
        {
          name: "Workflow Notifications",
          key: "vendor-nav-settings-workflow-notifications",
          route: "/afms/settings/workflow-notifications",
          requiredAction: "ViewAsset",
        },
      ],
    },
  ],
};

// Management Core group — the section a managing org uses to run its own
// clients/vendors. Visible to standalone and managing (enterprise) orgs, but
// hidden for orgs that are themselves MANAGED by an enterprise: a managed
// child (e.g. a vendor created under an enterprise) doesn't manage anyone,
// so the section makes no sense there. Children are routed within the AFMS
// portal so the user never leaves the shell they're in.
const enterpriseGroup = {
  type: "collapse",
  name: "Management Core",
  key: "vendor-nav-enterprise",
  icon: <ApartmentOutlinedIcon fontSize="small" />,
  requiresNotManaged: true,
  collapse: [
    {
      name: "Manage my Clients",
      key: "vendor-nav-enterprise-clients",
      route: "/afms/enterprise/manage-clients",
      icon: <HandshakeOutlinedIcon fontSize="small" />,
    },
    {
      name: "Manage my Vendors",
      key: "vendor-nav-enterprise-vendors",
      route: "/afms/enterprise/manage-vendors",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
    },
  ],
};

const environmentalPortalRoutes = [...primaryRoutes, settingsGroup, enterpriseGroup];

// True when the item's gate is satisfied by the caller's capabilities + org context.
export const itemPassesGate = (route, allowedActions, isPlatformAdmin, orgContext = {}) => {
  if (route.requiresVendorOrg && orgContext.isVendorOrg !== true) return false;
  // Per-org sidebar toggles from Organization.showEnvironmentalControlCenter
  // and Organization.showCommandCenter. Explicit `false` hides; anything
  // else (true / undefined / stale session) keeps the group visible.
  if (route.requiresEccVisible && orgContext.showEnvironmentalControlCenter === false) return false;
  if (route.requiresCommandCenterVisible && orgContext.showCommandCenter === false) return false;
  // afms-api ap-queue/access. Fail-open like the toggles above: only an
  // explicit "not allowed" hides it, not a slow or failed check.
  if (route.requiresApAccess && orgContext.apAccess === false) return false;
  // Hide from orgs that are managed by an enterprise (they don't manage
  // sub-orgs of their own). Fail-open: only an explicit isManaged===true hides.
  if (route.requiresNotManaged && orgContext.isManaged === true) return false;
  // The Enterprise menu requires BOTH the org-level isEnterprise flag
  // AND that the caller is the org's primary OWNER. Anything else (a
  // CUSTOMER user inside an enterprise org, an OWNER of a non-enterprise
  // org, etc.) hides it. Fail-closed: missing role/flag → false.
  if (route.requiresEnterpriseOwner) {
    const isOwner = (orgContext.role || "").toUpperCase() === "OWNER";
    const isEnterprise = orgContext.isEnterpriseOrganization === true;
    if (!isOwner || !isEnterprise) return false;
  }
  if (route.requiresPlatformAdmin) return isPlatformAdmin === true;
  if (route.requiredAction) return allowedActions.has(route.requiredAction);
  return true;
};

const filterRoutesByCapabilities = (routes, allowedActions, isPlatformAdmin, orgContext) =>
  routes
    .map((route) => {
      if (route.type === "title" || route.type === "divider") return route;
      if (!itemPassesGate(route, allowedActions, isPlatformAdmin, orgContext)) return null;
      if (!route.collapse) return route;

      const collapse = filterRoutesByCapabilities(
        route.collapse,
        allowedActions,
        isPlatformAdmin,
        orgContext
      );
      // A collapse group with no visible children and no route of its own
      // is dropped entirely.
      if (route.type === "collapse" && collapse.length === 0 && !route.route) return null;
      return { ...route, collapse };
    })
    .filter(Boolean);

// Drop a section title that is not followed by any real item (orphaned
// because its whole section was gated out).
const stripOrphanTitles = (routes) =>
  routes.filter((route, index) => {
    if (route.type !== "title") return true;
    for (let i = index + 1; i < routes.length; i += 1) {
      if (routes[i].type === "title") return false;
      if (routes[i].type !== "divider") return true;
    }
    return false;
  });

/**
 * Builds the AFMS/Environmental portal sidebar, gated against the caller's
 * afms portal capabilities (from GET /api/portal/my-capabilities) plus the
 * caller's org context (isVendorOrganization).
 *
 * @param {{portalRole?:string, allowedActions?:string[], isPlatformAdmin?:boolean}|null} capabilities
 *   When null/absent or with no resolved portalRole, capability-based gates
 *   are bypassed so the menu degrades to showing every cap-gated item —
 *   better than hiding the whole sidebar on a transient capabilities-fetch
 *   failure. Org-context gates (e.g. requiresVendorOrg) are still enforced.
 * @param {{isVendorOrg?:boolean}} [orgContext]
 *   Per-org flags surfaced from userDetails. Drives the requiresVendorOrg
 *   gate (the Vendor Command Center entry).
 */
export const getEnvironmentalPortalRoutes = (capabilities, orgContext = {}) => {
  if (!capabilities || !capabilities.portalRole) {
    // Capabilities not loaded yet — show every cap-gated item, but still
    // honor org-context gates so non-vendor orgs don't get the Vendor
    // Command Center entry on a transient capabilities miss.
    const openAllActions = {
      has: () => true,
    };
    return stripOrphanTitles(
      filterRoutesByCapabilities(environmentalPortalRoutes, openAllActions, true, orgContext)
    );
  }
  const allowedActions = new Set(capabilities.allowedActions || []);
  const isPlatformAdmin = capabilities.isPlatformAdmin === true;
  return stripOrphanTitles(
    filterRoutesByCapabilities(environmentalPortalRoutes, allowedActions, isPlatformAdmin, orgContext)
  );
};

export default environmentalPortalRoutes;
